import React, { useEffect, useState } from "react";
import EventsService from "../services/EventsService";

const GPIO_LABELS = { "5": "Button 1", "18": "Button 2", "19": "Button 3", "23": "Button 4" };
const GPIO_ICONS = { "5": "fa-hand-pointer", "18": "fa-hand-pointer", "19": "fa-hand-pointer", "23": "fa-hand-pointer" };
const CUSTOM_LABELS = { "custom-0": "Custom 1", "custom-1": "Custom 2", "custom-2": "Custom 3", "custom-3": "Custom 4" };

const ACTION_COLORS = {
  "5": "#30D158", "18": "#0A84FF", "19": "#FF453A", "23": "#FFD60A",
  "custom-0": "#BF5AF2", "custom-1": "#FF9F0A", "custom-2": "#64D2FF", "custom-3": "#FF375F",
};

function getActionLabel(typeId) {
  return GPIO_LABELS[typeId] || CUSTOM_LABELS[typeId] || `Action ${typeId}`;
}

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m > 0 && s > 0) return `${m}m ${s}s`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function AlarmsPage() {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [name, setName] = useState("");
  const [actionTypeId, setActionTypeId] = useState("5");
  const [minutes, setMinutes] = useState(2);
  const [seconds, setSeconds] = useState(30);
  const [isRecurring, setIsRecurring] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const fetchAlarms = async () => {
    try {
      const data = await EventsService.listGlobalAlarms();
      setAlarms(data || []);
    } catch {
      setAlarms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlarms(); }, []);

  const resetForm = () => {
    setName(""); setActionTypeId("5"); setMinutes(2); setSeconds(30);
    setIsRecurring(true); setEditingId(null); setShowForm(false);
  };

  const handleEdit = (alarm) => {
    setEditingId(alarm.id);
    setName(alarm.name || "");
    setActionTypeId(alarm.actionTypeId);
    setMinutes(Math.floor(alarm.durationSeconds / 60));
    setSeconds(alarm.durationSeconds % 60);
    setIsRecurring(alarm.isRecurring);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    const durationSeconds = minutes * 60 + seconds;
    if (durationSeconds < 1) return;
    try {
      if (editingId) {
        await EventsService.updateAlarm(editingId, { name, actionTypeId, durationSeconds, isRecurring });
      } else {
        await EventsService.createAlarm({ actionTypeId, durationSeconds, isRecurring, name });
      }
      resetForm();
      fetchAlarms();
    } catch {}
  };

  const handleDelete = async (id) => {
    try { await EventsService.deleteAlarm(id); fetchAlarms(); } catch {}
  };

  const handleToggle = async (alarm) => {
    try { await EventsService.updateAlarm(alarm.id, { enabled: !alarm.enabled }); fetchAlarms(); } catch {}
  };

  const typeIdOptions = [
    { value: "5", label: "Button 1" }, { value: "18", label: "Button 2" },
    { value: "19", label: "Button 3" }, { value: "23", label: "Button 4" },
    { value: "custom-0", label: "Custom Action 1" }, { value: "custom-1", label: "Custom Action 2" },
    { value: "custom-2", label: "Custom Action 3" }, { value: "custom-3", label: "Custom Action 4" },
  ];

  const styles = {
    container: {
      padding: "0 24px 40px",
      maxWidth: 960,
      margin: "0 auto",
    },
    subtitle: {
      color: "rgba(255,255,255,0.45)",
      fontSize: 14,
      lineHeight: 1.6,
      marginBottom: 28,
      maxWidth: 600,
    },
    addBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 20px",
      background: "linear-gradient(135deg, rgba(10,132,255,0.25), rgba(10,132,255,0.1))",
      border: "1px solid rgba(10,132,255,0.3)",
      borderRadius: 12,
      color: "#0A84FF",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
      marginBottom: 28,
    },
    formOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      animation: "fadeIn 0.2s ease",
    },
    formCard: {
      background: "rgba(22,22,38,0.95)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 20,
      padding: "28px 28px 24px",
      width: "100%",
      maxWidth: 420,
      boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
    },
    formTitle: {
      fontSize: 18,
      fontWeight: 700,
      color: "#fff",
      marginBottom: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    formCloseBtn: {
      background: "rgba(255,255,255,0.08)",
      border: "none",
      borderRadius: 8,
      color: "rgba(255,255,255,0.5)",
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: 16,
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: "rgba(255,255,255,0.5)",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: 6,
      display: "block",
    },
    formInput: {
      width: "100%",
      padding: "10px 14px",
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 10,
      color: "#fff",
      fontSize: 14,
      outline: "none",
      marginBottom: 16,
    },
    formSelect: {
      width: "100%",
      padding: "10px 14px",
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 10,
      color: "#fff",
      fontSize: 14,
      outline: "none",
      marginBottom: 16,
      appearance: "none",
    },
    durationRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 16,
    },
    durationInput: {
      width: 70,
      padding: "10px 8px",
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 10,
      color: "#fff",
      fontSize: 20,
      fontWeight: 700,
      textAlign: "center",
      outline: "none",
    },
    durationLabel: {
      fontSize: 13,
      color: "rgba(255,255,255,0.4)",
      fontWeight: 500,
    },
    toggleRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 22,
      cursor: "pointer",
    },
    toggleTrack: (on) => ({
      width: 44,
      height: 24,
      borderRadius: 12,
      background: on ? "#30D158" : "rgba(255,255,255,0.15)",
      position: "relative",
      transition: "background 0.2s",
      flexShrink: 0,
    }),
    toggleKnob: (on) => ({
      width: 20,
      height: 20,
      borderRadius: 10,
      background: "#fff",
      position: "absolute",
      top: 2,
      left: on ? 22 : 2,
      transition: "left 0.2s",
      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
    }),
    formSubmitBtn: {
      width: "100%",
      padding: "12px",
      background: "linear-gradient(135deg, #0A84FF, #0066CC)",
      border: "none",
      borderRadius: 12,
      color: "#fff",
      fontSize: 15,
      fontWeight: 700,
      cursor: "pointer",
      transition: "opacity 0.2s",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: 16,
    },
    card: (color, enabled) => ({
      background: "rgba(255,255,255,0.04)",
      border: `1px solid ${enabled ? color + "33" : "rgba(255,255,255,0.06)"}`,
      borderRadius: 16,
      padding: "20px 22px",
      position: "relative",
      overflow: "hidden",
      opacity: enabled ? 1 : 0.55,
      transition: "all 0.3s",
    }),
    cardGlow: (color) => ({
      position: "absolute",
      top: -40,
      right: -40,
      width: 100,
      height: 100,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
      pointerEvents: "none",
    }),
    cardHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: 14,
    },
    cardActionBadge: (color) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      background: color + "18",
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      color: color,
    }),
    cardDot: (color) => ({
      width: 7,
      height: 7,
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
    }),
    cardStatusPill: (enabled) => ({
      padding: "4px 10px",
      borderRadius: 8,
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      background: enabled ? "rgba(48,209,88,0.15)" : "rgba(255,69,58,0.12)",
      color: enabled ? "#30D158" : "#FF453A",
      cursor: "pointer",
      border: "none",
    }),
    cardName: {
      fontSize: 15,
      fontWeight: 600,
      color: "#fff",
      marginBottom: 4,
    },
    cardDuration: {
      fontSize: 32,
      fontWeight: 800,
      color: "#fff",
      lineHeight: 1.1,
      marginBottom: 6,
      letterSpacing: "-0.5px",
    },
    cardMeta: {
      fontSize: 12,
      color: "rgba(255,255,255,0.35)",
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    cardActions: {
      display: "flex",
      gap: 8,
    },
    cardBtn: {
      flex: 1,
      padding: "8px 0",
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 10,
      color: "rgba(255,255,255,0.7)",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
    },
    cardBtnDanger: {
      flex: 1,
      padding: "8px 0",
      background: "rgba(255,69,58,0.08)",
      border: "1px solid rgba(255,69,58,0.15)",
      borderRadius: 10,
      color: "#FF453A",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
    },
    empty: {
      textAlign: "center",
      padding: "80px 20px",
    },
    emptyIcon: {
      fontSize: 48,
      color: "rgba(255,255,255,0.1)",
      marginBottom: 16,
    },
    emptyText: {
      fontSize: 16,
      color: "rgba(255,255,255,0.3)",
      fontWeight: 500,
      marginBottom: 4,
    },
    emptyHint: {
      fontSize: 13,
      color: "rgba(255,255,255,0.2)",
    },
  };

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            <a id="sidebar-toggle" onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }} href="#">
              <i className="fas fa-bars"></i>
            </a>
            <h5>
              <i className="fas fa-bell" style={{ marginRight: 8 }}></i>
              Global Alarms
            </h5>
          </div>
        </nav>
      </div>

      <div className="main-wrapper" style={styles.container}>
        <p style={styles.subtitle}>
          Alarms fire when an action isn't triggered within the set duration.
          They apply automatically to every event with a matching action.
        </p>

        <button style={styles.addBtn} onClick={() => { resetForm(); setShowForm(true); }}>
          <i className="fas fa-plus"></i>
          New Alarm
        </button>

        {/* Modal Form */}
        {showForm && (
          <div style={styles.formOverlay} onClick={(e) => { if (e.target === e.currentTarget) resetForm(); }}>
            <div style={styles.formCard}>
              <div style={styles.formTitle}>
                <span>{editingId ? "Edit Alarm" : "New Alarm"}</span>
                <button style={styles.formCloseBtn} onClick={resetForm}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <label style={styles.formLabel}>Name</label>
              <input
                style={styles.formInput}
                placeholder="e.g. Hydration reminder"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <label style={styles.formLabel}>Action</label>
              <select style={styles.formSelect} value={actionTypeId} onChange={(e) => setActionTypeId(e.target.value)}>
                {typeIdOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <label style={styles.formLabel}>Duration</label>
              <div style={styles.durationRow}>
                <input
                  style={styles.durationInput}
                  type="number" min={0} max={59}
                  value={minutes}
                  onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                />
                <span style={styles.durationLabel}>min</span>
                <input
                  style={styles.durationInput}
                  type="number" min={0} max={59}
                  value={seconds}
                  onChange={(e) => setSeconds(parseInt(e.target.value) || 0)}
                />
                <span style={styles.durationLabel}>sec</span>
              </div>

              <div style={styles.toggleRow} onClick={() => setIsRecurring(!isRecurring)}>
                <div style={styles.toggleTrack(isRecurring)}>
                  <div style={styles.toggleKnob(isRecurring)} />
                </div>
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>Recurring</span>
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>
                  {isRecurring ? "Resets after each trigger" : "Fires once then stops"}
                </span>
              </div>

              <button style={styles.formSubmitBtn} onClick={handleSubmit}>
                {editingId ? "Save Changes" : "Create Alarm"}
              </button>
            </div>
          </div>
        )}

        {/* Alarms Grid */}
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : alarms.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}><i className="fas fa-bell-slash"></i></div>
            <div style={styles.emptyText}>No alarms yet</div>
            <div style={styles.emptyHint}>Create one to get reminders during activities</div>
          </div>
        ) : (
          <div style={styles.grid}>
            {alarms.map((alarm) => {
              const color = ACTION_COLORS[alarm.actionTypeId] || "#0A84FF";
              const label = alarm.name || getActionLabel(alarm.actionTypeId);
              return (
                <div key={alarm.id} style={styles.card(color, alarm.enabled)}>
                  <div style={styles.cardGlow(color)} />
                  <div style={styles.cardHeader}>
                    <div style={styles.cardActionBadge(color)}>
                      <div style={styles.cardDot(color)} />
                      {getActionLabel(alarm.actionTypeId)}
                    </div>
                    <button style={styles.cardStatusPill(alarm.enabled)} onClick={() => handleToggle(alarm)}>
                      {alarm.enabled ? "Active" : "Off"}
                    </button>
                  </div>
                  <div style={styles.cardName}>{label}</div>
                  <div style={styles.cardDuration}>{formatDuration(alarm.durationSeconds)}</div>
                  <div style={styles.cardMeta}>
                    <i className={`fas ${alarm.isRecurring ? "fa-sync-alt" : "fa-clock"}`} style={{ fontSize: 10 }}></i>
                    {alarm.isRecurring ? "Recurring" : "One-time"}
                  </div>
                  <div style={styles.cardActions}>
                    <button style={styles.cardBtn} onClick={() => handleEdit(alarm)}>
                      <i className="fas fa-pen" style={{ marginRight: 6, fontSize: 11 }}></i>Edit
                    </button>
                    <button style={styles.cardBtnDanger} onClick={() => handleDelete(alarm.id)}>
                      <i className="fas fa-trash" style={{ marginRight: 6, fontSize: 11 }}></i>Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default AlarmsPage;
